import { useEffect } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { authTokenAtom } from "atoms/authAtoms";
import { stageConfigAtom } from "atoms/Rtool/stageConfigAtoms";
import { errorAtom } from "atoms/error";
import {
    allConditionDataAtom,
    allConditionLoadingAtom,
    MtgVibData,
} from "atoms/Rtool/mtgAtoms";
import { createEmptyAllConditionData } from "./initializer";
import { fetchGraphData, fetchGraphDataHev } from "api/fetchRtool";
import { powertrainAtom } from "atoms/powertrainAtom";
import { gearIdAtom } from "atoms/gearAtom";

export function useAllConditionDataSetter(conditionIds: string[]) {
    const authToken = useAtomValue(authTokenAtom);
    const config = useAtomValue(stageConfigAtom);
    const powertrain = useAtomValue(powertrainAtom);
    const gearId = useAtomValue(gearIdAtom);
    const isHev = powertrain === "hev";
    const setAllConditionData = useSetAtom(allConditionDataAtom);
    const setLoading = useSetAtom(allConditionLoadingAtom);
    const setError = useSetAtom(errorAtom);

    // ローディング状態の初期データをセット
    useEffect(() => {
        const initMap = createEmptyAllConditionData(conditionIds);
        setAllConditionData(initMap);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setAllConditionData]);

    // APIから全条件のデータを取得
    useEffect(() => {
        if (!config.project_id || !authToken) return;
        // HEV は gear 単位で取得。gear 未選択時は呼ばない（コンポーネントが選択を促す）
        if (isHev && !gearId) return;

        let cancelled = false;

        const fetchAllConditionData = async () => {
            setLoading(true);
            setAllConditionData(createEmptyAllConditionData(conditionIds));
            try {
                const results = await Promise.all(
                    conditionIds.map(async (id) => {
                        try {
                            const data = isHev
                                ? await fetchGraphDataHev(
                                    authToken,
                                    { ...config, condition_id: id, graph_id: "mtg_vib_pred" },
                                    gearId!
                                )
                                : await fetchGraphData(
                                    authToken,
                                    { ...config, condition_id: id, graph_id: "mtg_vib_pred" }
                                );
                            return { condition_id: id, data, status: "success" } as const;
                        } catch (err) {
                            console.error(`fetchGraphData failed for ${id}:`, err);
                            return {
                                condition_id: id,
                                data: { cabin_noise_target: [], cabin_noise_pred: [] },
                                status: "error",
                            } as const;
                        }
                    })
                );

                const resultMap = results.reduce<Record<string, MtgVibData>>(
                    (acc, { condition_id, data, status }) => {
                        acc[condition_id] = { data, status };
                        return acc;
                    },
                    {}
                );

                // 3) Atom を辞書で更新
                if (!cancelled) {
                    setAllConditionData(resultMap);
                }
            } catch (unhandled) {
                if (!cancelled) {
                    setError(`Unexpected error in fetching all data: ${unhandled}`);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchAllConditionData();

        return () => {
            cancelled = true;
            setLoading(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken, config, isHev, gearId, setAllConditionData, setError, setLoading]);
};
